import { create } from 'zustand';
import { cx } from './cx';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';
export type ToastItem = { id: number; kind: ToastKind; text: string; ttl: number };

type ToastStore = {
  toasts: ToastItem[];
  push: (text: string, kind?: ToastKind, ttl?: number) => number;
  dismiss: (id: number) => void;
};

let nextId = 1;

export const useToastStore = create<ToastStore>()((set) => ({
  toasts: [],
  push(text, kind = 'info', ttl = 4000) {
    const id = nextId++;
    set((s) => ({ toasts: [...s.toasts.slice(-4), { id, kind, text, ttl }] }));
    if (ttl > 0) setTimeout(() => useToastStore.getState().dismiss(id), ttl);
    return id;
  },
  dismiss(id) {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));

/** Imperative API usable outside React: toast.success('Saved!'). */
export const toast = {
  info: (text: string, ttl?: number) => useToastStore.getState().push(text, 'info', ttl),
  success: (text: string, ttl?: number) => useToastStore.getState().push(text, 'success', ttl),
  warning: (text: string, ttl?: number) => useToastStore.getState().push(text, 'warning', ttl),
  error: (text: string, ttl?: number) => useToastStore.getState().push(text, 'error', ttl ?? 6000),
};

const ICON: Record<ToastKind, string> = { info: 'ℹ️', success: '✨', warning: '⚠️', error: '💥' };

/** Renders the toast stack. Mounted once in App.tsx. */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  if (toasts.length === 0) return null;
  return (
    <div className="toaster" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <div key={t.id} className={cx('toast', `toast-${t.kind}`)} role={t.kind === 'error' ? 'alert' : 'status'}>
          <span aria-hidden="true">{ICON[t.kind]}</span>
          <div className="toast-body">{t.text}</div>
          <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
